import type { HandoverBlockKey } from '@/pages/handover';
import { apiJson } from './apiFetch';

export type HandoverTemplateItemType = 'follow_up' | 'checkbox' | 'datetime' | 'number' | 'text' | 'table';
export type HandoverTemplateColumnType = 'number' | 'checkbox' | 'text';

export interface HandoverTemplateColumn {
  id: string;
  label: string;
  type: HandoverTemplateColumnType;
}

export interface HandoverTemplateRow {
  id: string;
  label: string;
}

export interface HandoverTemplateItem {
  /** Generated by the API when missing; an id sent back is kept. */
  id: string;
  type: HandoverTemplateItemType;
  label: string;
  /** Table only. */
  columns?: HandoverTemplateColumn[];
  /** Table only: the fixed first column, one label per row. */
  rows?: HandoverTemplateRow[];
}

export interface HandoverTemplate {
  id: string;
  title: string;
  description: string | null;
  isActive: boolean;
  /** Every store of the account, including stores created later; `storeIds` is then empty. */
  isAllStores: boolean;
  storeIds: string[];
  /** In registration order, the order every handover shows them in. */
  items: HandoverTemplateItem[];
  followUpCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface HandoverTemplatePayload {
  title: string;
  description: string | null;
  isActive: boolean;
  isAllStores: boolean;
  storeIds: string[];
  /** Ids the template already has are kept; the client names new items, columns and rows itself. */
  items: HandoverTemplateItem[];
}

export interface HandoverBlockSetting {
  blockKey: HandoverBlockKey;
  isEnabled: boolean;
  /** Whether the account feature the block reads from is on (true for a block without one). */
  isAvailable: boolean;
  /** The account feature the block needs, or null. */
  feature: string | null;
}

export type HandoverBlockSettingPatch = Pick<HandoverBlockSetting, 'blockKey' | 'isEnabled'>;

export const HANDOVER_BLOCK_SETTINGS_QUERY_KEY = ['handover-block-settings'] as const;
export const HANDOVER_TEMPLATES_QUERY_KEY = ['handover-templates'] as const;

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  ...(body !== undefined && { body: JSON.stringify(body) }),
});

const templatePath = (templateId: string): string => `/handover-templates/${encodeURIComponent(templateId)}`;

/** The seven Khova blocks in the API's order. */
export const getHandoverBlockSettings = async (): Promise<HandoverBlockSetting[]> =>
  (await apiJson<{ blocks: HandoverBlockSetting[] }>('/handover-block-settings')).blocks;

/** Partial: only the blocks sent change. Answers every block, as the GET does. */
export const putHandoverBlockSettings = async (blocks: HandoverBlockSettingPatch[]): Promise<HandoverBlockSetting[]> =>
  (await apiJson<{ blocks: HandoverBlockSetting[] }>('/handover-block-settings', json('PUT', { blocks }))).blocks;

export const getHandoverTemplates = async (): Promise<HandoverTemplate[]> =>
  (await apiJson<{ templates: HandoverTemplate[] }>('/handover-templates')).templates;

export const createHandoverTemplate = async (payload: HandoverTemplatePayload): Promise<HandoverTemplate> =>
  (await apiJson<{ template: HandoverTemplate }>('/handover-templates', json('POST', payload))).template;

export const updateHandoverTemplate = async (
  templateId: string,
  payload: HandoverTemplatePayload,
): Promise<HandoverTemplate> =>
  (await apiJson<{ template: HandoverTemplate }>(templatePath(templateId), json('PUT', payload))).template;

export const deleteHandoverTemplate = (templateId: string): Promise<void> =>
  apiJson<void>(templatePath(templateId), json('DELETE'));
